/**
 * 歌词版播放策略 - 音频+歌词同步
 * 使用 AudioController 播放音频，同时同步显示歌词
 */
import { PlayStrategy } from './PlayStrategy.js';
import AudioController from '../AudioController.js';
import {
  isExternalPluginSource,
  isPlatformAccountSource,
  getExternalPluginMediaSource,
  getExternalPluginLyric
} from '../../music/plugins/externalPluginResolver.js';

const isPluginResolvableSong = (song) =>
  isExternalPluginSource(song?.source) || isPlatformAccountSource(song?.source) || song?.songmid || song?.mid;

export class LyricsStrategy extends PlayStrategy {
  constructor() {
    super();
    
    this._loadToken = 0;
    this._destroyed = false;
    
    // 🔥 每次创建新策略时，创建新的 AudioController 实例
    // 确保 audioController 一定被初始化
    this.audioController = new AudioController();
    
    if (!this.audioController) {
      throw new Error('无法创建 AudioController 实例');
    }
    
    // 歌词相关状态
    this.lyricsData = null; // 歌词数据
    this.currentLyricIndex = -1; // 当前歌词索引
    this.lyricsCallbacks = []; // 歌词更新回调
    
    // 绑定基础播放器的事件到策略事件系统
    this.bindAudioControllerEvents();
    
    console.log('🎵 [LyricsStrategy] 歌词版策略创建完成');
  }
  
  /**
   * 绑定 AudioController 事件到策略事件系统
   */
  bindAudioControllerEvents() {
    // 播放事件
    this.audioController.on('play', () => {
      this.emit('play', {});
      this.startLyricsSync();
    });
    
    // 🔥 新增：正在播放事件（音频真正开始播放时触发，用户能听到声音）
    // 这是最准确的播放状态标志，用于更新全局播放状态
    this.audioController.on('playing', () => {
      this.emit('playing', {});
    });
    
    // 暂停事件
    this.audioController.on('pause', () => {
      this.emit('pause', {});
      this.pauseLyricsSync();
    });
    
    // 时间更新事件（用于歌词同步）
    this.audioController.on('timeupdate', (data) => {
      this.emit('timeupdate', {
        currentTime: data.currentTime,
        duration: data.duration
      });
      
      // 同步歌词到当前播放时间
      this.syncLyricsToTime(data.currentTime);
    });
    
    // 播放结束事件
    this.audioController.on('ended', () => {
      this.emit('ended', {});
      this.stopLyricsSync();
    });
    
    // 错误事件
    this.audioController.on('error', (data) => {
      this.emit('error', { error: data.error });
    });
    
    // 元数据加载完成事件
    this.audioController.on('loadedmetadata', (data) => {
      this.emit('loadedmetadata', { duration: data.duration });
    });
    
    // 可以播放事件
    this.audioController.on('canplay', () => {
      this.emit('canplay', {});
    });
  }
  
  /**
   * 加载歌曲
   * @param {Object} song - 歌曲对象
   * @returns {Promise<void>}
   */
  isLoadActive(loadToken) {
    return !this._destroyed && loadToken === this._loadToken && !!this.audioController;
  }

  async load(song) {
    if (!song) {
      throw new Error('歌曲对象不能为空');
    }

    const loadToken = ++this._loadToken;
    
    console.log('🎵 [LyricsStrategy] 加载歌词版歌曲:', song.title, {
      songId: song.id,
      videoId: song.videoId
    });
    
    // 🔥 关键修复：直接使用 songId 传递给 AudioController
    // AudioController 现在支持直接接收 songId（数字或字符串），无需构造 URL
    // AudioController 会按照优先级处理：
    // 1. 首先检查缓存（使用 userId + songId）
    // 2. 如果没有缓存，尝试获取 OneDrive 直链（使用 songId）
    // 3. 最后回退到流式接口（使用 songId 构造的 URL）
    
    // 获取歌曲ID
    const songId = song.id;
    if (!songId) {
      throw new Error(`歌曲缺少ID (song: ${JSON.stringify(song)})`);
    }
    
    // 🔥 对于外部音乐，如果有 playUrl 则直接使用 URL
    // 对于内部音乐，传递整个歌曲对象给 AudioController，让它根据 audio_storage_type 优化加载策略
    let loadPromise;
    
    // 🚀 优化：如果是QQ音乐插件的在线歌曲，先通过插件获取音频URL
    if (song.isOnline || song.isExternalWithResources) {
      // 尝试从多个属性获取播放URL
      let audioUrl = song.playUrl || song.streamUrl || song.url;
      
      if (audioUrl) {
        console.log('🌐 [LyricsStrategy] 使用在线音乐的播放URL:', audioUrl);
        loadPromise = this.audioController.loadSong(audioUrl);
      } else {
        // 🔥 新增：如果是QQ音乐插件的在线音乐，尝试通过插件获取播放URL
        if (isPluginResolvableSong(song)) {
          console.log('🎵 [LyricsStrategy] 检测到插件音源在线歌曲，尝试获取播放URL...', song.source);
          try {
            const mediaSource = await getExternalPluginMediaSource(song, 'high');
            if (!this.isLoadActive(loadToken)) {
              console.log('🎵 [LyricsStrategy] 策略已切换，忽略插件音源加载结果');
              return;
            }
            if (mediaSource && mediaSource.url) {
              audioUrl = mediaSource.url;
              song.playUrl = mediaSource.url;
              song.streamUrl = mediaSource.url;
              song.url = mediaSource.url;
              console.log('✅ [LyricsStrategy] 成功从插件音源获取播放URL:', audioUrl);
              
              // 🚀 优化：如果 getMediaSource 同时返回了歌词，保存到歌曲对象上
              if (mediaSource.lyric) {
                console.log('🎵 [LyricsStrategy] getMediaSource 同时返回了歌词，保存到歌曲对象');
                song._cachedLyric = mediaSource.lyric;
                // 🔥 修复：不要将lyricsUrl设置为lrc字符串，而是标记为已有歌词
                // DXHPlayer会优先检查_cachedLyric，不需要通过lyricsUrl判断
                if (!song.lyricsUrl) {
                  song.lyricsUrl = 'cached'; // 标记为已缓存，但不存储实际内容
                }
                console.log('🎵 [LyricsStrategy] 歌词已保存，_cachedLyric.lrc 长度:', mediaSource.lyric?.lrc?.length || 0);
              }
              
              loadPromise = this.audioController.loadSong(audioUrl);
            } else {
              throw new Error('无法从插件音源获取播放URL');
            }
          } catch (error) {
            if (!this.isLoadActive(loadToken)) {
              console.log('🎵 [LyricsStrategy] 策略已切换，忽略插件音源加载错误');
              return;
            }
            console.error('❌ [LyricsStrategy] 从插件音源获取播放URL失败:', error);
            if (isPluginResolvableSong(song)) {
              throw error instanceof Error ? error : new Error(String(error?.message || error));
            }
            // 回退到传递歌曲对象，让 AudioController 尝试其他方式
            console.log('🎵 [LyricsStrategy] 回退到传递歌曲对象给 AudioController');
            loadPromise = this.audioController.loadSong(song);
          }
        } else if (song.isExternalWithResources && song.playUrl) {
          // 外部音乐：直接使用 playUrl
          console.log('🎵 [LyricsStrategy] 外部音乐，使用 playUrl:', song.playUrl);
          loadPromise = this.audioController.loadSong(song.playUrl);
        } else if (song.streamUrl) {
          // 如果歌曲已有 streamUrl，使用它
          console.log('🎵 [LyricsStrategy] 使用歌曲的 streamUrl:', song.streamUrl);
          loadPromise = this.audioController.loadSong(song.streamUrl);
        } else {
          // 回退到传递歌曲对象
          console.log('🎵 [LyricsStrategy] 传递歌曲对象给 AudioController，它将根据 audio_storage_type 优化加载');
          loadPromise = this.audioController.loadSong(song);
        }
      }
    } else if (song.isExternalWithResources && song.playUrl) {
      // 外部音乐：直接使用 playUrl
      console.log('🎵 [LyricsStrategy] 外部音乐，使用 playUrl:', song.playUrl);
      loadPromise = this.audioController.loadSong(song.playUrl);
    } else if (song.streamUrl) {
      // 如果歌曲已有 streamUrl，使用它
      console.log('🎵 [LyricsStrategy] 使用歌曲的 streamUrl:', song.streamUrl);
      loadPromise = this.audioController.loadSong(song.streamUrl);
    } else {
      // 内部音乐：传递整个歌曲对象，AudioController 会根据 audio_storage_type 优化加载
      console.log('🎵 [LyricsStrategy] 传递歌曲对象给 AudioController，它将根据 audio_storage_type 优化加载');
      console.log('🎵 [LyricsStrategy] 歌曲信息:', {
        songId: song.id,
        audio_storage_type: song.audio_storage_type || song.audioStorageType || song.storageType,
        storageTypeDesc: song.audio_storage_type === 1 ? 'OneDrive' : song.audio_storage_type === 0 ? '本地' : '未知'
      });
      loadPromise = this.audioController.loadSong(song);
    }
    
    // 并行加载音频和歌词
    const loadPromises = [loadPromise];
    
    // 🚀 优化：如果有歌词或是在线歌曲，都尝试加载歌词
    // 在线歌曲可能通过 getMediaSource 已经获取了歌词（缓存在 _cachedLyric 中）
    const shouldLoadLyrics = song.lyricsUrl || song.lyrics || song._cachedLyric || 
                             (song.isOnline || song.isExternalWithResources);
    if (shouldLoadLyrics) {
      loadPromises.push(this.loadLyrics(song));
    }
    
    try {
      await Promise.all(loadPromises);
    } catch (error) {
      if (!this.isLoadActive(loadToken) || error?.code === 'LOAD_SUPERSEDED') {
        console.log('🎵 [LyricsStrategy] 策略已切换，忽略加载错误');
        return;
      }
      throw error;
    }

    if (!this.isLoadActive(loadToken)) {
      console.log('🎵 [LyricsStrategy] 策略已切换，跳过加载完成回调');
      return;
    }
    console.log('🎵 [LyricsStrategy] 音频和歌词加载完成');
  }
  
  /**
   * 将歌词同步到歌曲对象，供 DXHPlayer 通过 watch(_cachedLyric) 渲染
   * @param {Object} song
   * @param {{ lrc: string, tlyric?: string, rlyric?: string }} lyricData
   */
  syncLyricToSong(song, lyricData) {
    if (!song || !lyricData?.lrc) {
      return;
    }
    song._cachedLyric = {
      lrc: lyricData.lrc,
      tlyric: lyricData.tlyric || '',
      rlyric: lyricData.rlyric || ''
    };
    this.lyricsData = this.parseLyrics(lyricData.lrc);
  }

  /**
   * 将 LRC 文本同步到歌曲对象
   * @param {Object} song
   * @param {string} lyricsText
   */
  syncLyricTextToSong(song, lyricsText) {
    if (!song || !lyricsText) {
      return;
    }
    song._cachedLyric = {
      lrc: lyricsText,
      tlyric: '',
      rlyric: ''
    };
    this.lyricsData = this.parseLyrics(lyricsText);
  }

  /**
   * 加载歌词
   * @param {Object} song - 歌曲对象
   * @returns {Promise<void>}
   */
  async loadLyrics(song) {
    try {
      // 🚀 优化：优先使用缓存的歌词（如果 getMediaSource 已经返回了歌词）
      if (song._cachedLyric && song._cachedLyric.lrc) {
        this.lyricsData = this.parseLyrics(song._cachedLyric.lrc);
        console.log('🎵 [LyricsStrategy] 使用缓存的歌词（来自 getMediaSource），共', this.lyricsData.length, '行');
        return;
      }
      
      // 如果歌曲对象中已经有歌词数据
      if (song.lyrics) {
        this.syncLyricTextToSong(song, song.lyrics);
        console.log('🎵 [LyricsStrategy] 从歌曲对象加载歌词，共', this.lyricsData.length, '行');
        return;
      }
      
      // 如果有歌词URL，从URL加载
      if (song.lyricsUrl) {
        const ALIYUN_LYRICS_PREFIX = 'aliyun-lyrics:';
        if (song.lyricsUrl.startsWith(ALIYUN_LYRICS_PREFIX)) {
          const fileId = song.lyricsUrl.slice(ALIYUN_LYRICS_PREFIX.length);
          console.log('☁️ [LyricsStrategy] 检测到阿里云盘歌词标识，走 lyrics-url API');
          const myRequest = (await import('@/utils/myRequest')).default;
          const resp = await myRequest.get(`/api/aliyun/media/lyrics-url/${fileId}`);
          const downloadUrl = resp?.url;
          if (!downloadUrl) {
            throw new Error('阿里云盘歌词直链为空');
          }
          const lyricsResponse = await fetch(downloadUrl, {
            method: 'GET',
            referrerPolicy: 'no-referrer',
            headers: { Accept: 'text/plain,text/*,*/*' }
          });
          if (!lyricsResponse.ok) {
            throw new Error(`HTTP ${lyricsResponse.status}`);
          }
          const lyricsText = await lyricsResponse.text();
          this.syncLyricTextToSong(song, lyricsText);
          console.log('☁️ [LyricsStrategy] 阿里云盘歌词加载成功，共', this.lyricsData.length, '行');
          return;
        }

        // 内联歌词（历史降级数据）
        if (song.lyricsUrl.includes('\n') && song.lyricsUrl.length > 50) {
          this.syncLyricTextToSong(song, song.lyricsUrl);
          console.log('🎵 [LyricsStrategy] 使用内联歌词，共', this.lyricsData.length, '行');
          return;
        }

        // 🚀 优化：如果 lyricsUrl 是空字符串（可能是占位符），跳过
        if (song.lyricsUrl.trim() === '' || song.lyricsUrl === 'cached') {
          console.log('🎵 [LyricsStrategy] lyricsUrl 为空字符串，跳过URL加载');
          // 如果是QQ音乐插件的在线歌曲，尝试通过插件获取歌词
          if (isPluginResolvableSong(song)) {
            console.log('🎵 [LyricsStrategy] 尝试通过插件音源获取歌词...', song.source);
            try {
              const lyricData = await getExternalPluginLyric(song, { cachedLyric: song._cachedLyric });
              if (lyricData && lyricData.lrc) {
                this.syncLyricToSong(song, lyricData);
                console.log('🎵 [LyricsStrategy] 成功从插件音源获取歌词，共', this.lyricsData.length, '行');
                return;
              }
            } catch (error) {
              console.error('❌ [LyricsStrategy] 从插件音源获取歌词失败:', error);
            }
          }
          this.lyricsData = [];
          return;
        }
        
        const response = await fetch(song.lyricsUrl);
        const lyricsText = await response.text();
        this.syncLyricTextToSong(song, lyricsText);
        console.log('🎵 [LyricsStrategy] 从URL加载歌词，共', this.lyricsData.length, '行');
        return;
      }
      
      // 🚀 优化：如果是QQ音乐插件的在线歌曲，尝试通过插件获取歌词
      if ((song.isOnline || song.isExternalWithResources) && isPluginResolvableSong(song)) {
        console.log('🎵 [LyricsStrategy] 在线歌曲无歌词URL，尝试通过插件音源获取歌词...', song.source);
        try {
          const lyricData = await getExternalPluginLyric(song, { cachedLyric: song._cachedLyric });
          if (lyricData && lyricData.lrc) {
            this.syncLyricToSong(song, lyricData);
            console.log('🎵 [LyricsStrategy] 成功从插件音源获取歌词，共', this.lyricsData.length, '行');
            return;
          }
        } catch (error) {
          console.error('❌ [LyricsStrategy] 从插件音源获取歌词失败:', error);
        }
      }
      
      // 如果没有歌词，创建空的歌词数据
      this.lyricsData = [];
      console.log('🎵 [LyricsStrategy] 歌曲没有歌词数据');
    } catch (error) {
      console.error('🎵 [LyricsStrategy] 加载歌词失败:', error);
      this.lyricsData = [];
    }
  }
  
  /**
   * 解析歌词文本（LRC格式）
   * @param {string} lyricsText - 歌词文本
   * @returns {Array} 歌词数组，格式：[{ time: number, text: string }, ...]
   */
  parseLyrics(lyricsText) {
    if (!lyricsText) return [];
    
    const lines = lyricsText.split('\n');
    const lyrics = [];
    
    // LRC格式：[mm:ss.xx]歌词文本
    const timeRegex = /\[(\d{2}):(\d{2})\.(\d{2})\]/;
    
    lines.forEach(line => {
      const match = line.match(timeRegex);
      if (match) {
        const minutes = parseInt(match[1], 10);
        const seconds = parseInt(match[2], 10);
        const centiseconds = parseInt(match[3], 10);
        const time = minutes * 60 + seconds + centiseconds / 100;
        const text = line.replace(timeRegex, '').trim();
        
        if (text) {
          lyrics.push({ time, text });
        }
      }
    });
    
    // 按时间排序
    lyrics.sort((a, b) => a.time - b.time);
    
    return lyrics;
  }
  
  /**
   * 同步歌词到当前播放时间
   * @param {number} currentTime - 当前播放时间（秒）
   */
  syncLyricsToTime(currentTime) {
    if (!this.lyricsData || this.lyricsData.length === 0) {
      return;
    }
    
    // 找到当前时间对应的歌词索引
    let newIndex = -1;
    for (let i = this.lyricsData.length - 1; i >= 0; i--) {
      if (currentTime >= this.lyricsData[i].time) {
        newIndex = i;
        break;
      }
    }
    
    // 如果歌词索引发生变化，通知回调
    if (newIndex !== this.currentLyricIndex) {
      this.currentLyricIndex = newIndex;
      this.notifyLyricsChange(newIndex);
    }
  }
  
  /**
   * 通知歌词变化
   * @param {number} index - 当前歌词索引
   */
  notifyLyricsChange(index) {
    if (index >= 0 && index < this.lyricsData.length) {
      const lyric = this.lyricsData[index];
      this.lyricsCallbacks.forEach(callback => {
        try {
          callback(lyric, index);
        } catch (error) {
          console.error('🎵 [LyricsStrategy] 歌词回调错误:', error);
        }
      });
      
      // 触发歌词更新事件
      this.emit('lyricsUpdate', {
        lyric: lyric.text,
        time: lyric.time,
        index: index
      });
    }
  }
  
  /**
   * 开始歌词同步
   */
  startLyricsSync() {
    // 歌词同步已经在 timeupdate 事件中处理
    console.log('🎵 [LyricsStrategy] 开始歌词同步');
  }
  
  /**
   * 暂停歌词同步
   */
  pauseLyricsSync() {
    console.log('🎵 [LyricsStrategy] 暂停歌词同步');
  }
  
  /**
   * 停止歌词同步
   */
  stopLyricsSync() {
    this.currentLyricIndex = -1;
    console.log('🎵 [LyricsStrategy] 停止歌词同步');
  }
  
  /**
   * 注册歌词更新回调
   * @param {Function} callback - 回调函数 (lyric, index) => void
   */
  onLyricsUpdate(callback) {
    this.lyricsCallbacks.push(callback);
  }
  
  /**
   * 移除歌词更新回调
   * @param {Function} callback - 回调函数
   */
  offLyricsUpdate(callback) {
    const index = this.lyricsCallbacks.indexOf(callback);
    if (index > -1) {
      this.lyricsCallbacks.splice(index, 1);
    }
  }
  
  /**
   * 获取当前歌词
   * @returns {Object|null} 当前歌词对象 { time, text }
   */
  getCurrentLyric() {
    if (this.currentLyricIndex >= 0 && this.currentLyricIndex < this.lyricsData.length) {
      return this.lyricsData[this.currentLyricIndex];
    }
    return null;
  }
  
  /**
   * 获取所有歌词
   * @returns {Array} 歌词数组
   */
  getAllLyrics() {
    return this.lyricsData || [];
  }
  
  /**
   * 播放
   * @returns {Promise<void>}
   */
  async play() {
    console.log('🎵 [LyricsStrategy] 开始播放（音频+歌词）');
    
    if (!this.audioController) {
      throw new Error('音频控制器不存在，无法播放');
    }
    
    await this.audioController.play();
  }
  
  /**
   * 暂停
   */
  pause() {
    console.log('🎵 [LyricsStrategy] 暂停播放');
    this.audioController.pause();
  }
  
  /**
   * 停止
   */
  stop() {
    console.log('🎵 [LyricsStrategy] 停止播放');
    this.audioController.stop();
    this.stopLyricsSync();
  }
  
  /**
   * 设置播放时间
   * @param {number} time - 播放时间（秒）
   */
  setCurrentTime(time) {
    this.audioController.setCurrentTime(time);
    // 同步歌词到新时间
    this.syncLyricsToTime(time);
  }
  
  /**
   * 获取当前播放时间
   * @returns {number}
   */
  getCurrentTime() {
    if (!this.audioController) {
      return 0;
    }
    return this.audioController.getCurrentTime();
  }
  
  /**
   * 获取总时长
   * @returns {number}
   */
  getDuration() {
    if (!this.audioController) {
      return 0;
    }
    return this.audioController.getDuration();
  }
  
  /**
   * 是否正在播放
   * @returns {boolean}
   */
  isPlaying() {
    if (!this.audioController) {
      return false;
    }
    return this.audioController.isPlaying();
  }
  
  /**
   * 🔥 新增：全屏相关方法
   */
  
  /**
   * 检查是否处于全屏状态
   * @returns {boolean}
   */
  isInFullscreen() {
    if (this.fullscreenControls && this.fullscreenControls.isInFullscreen) {
      return this.fullscreenControls.isInFullscreen('lyrics');
    }
    return false;
  }
  
  /**
   * 进入全屏（如果需要的话）
   * 当切换歌曲时，如果之前处于全屏状态，会调用此方法
   * @returns {Promise<void>}
   */
  async enterFullscreenIfNeeded() {
    if (this.fullscreenControls && this.fullscreenControls.enterFullscreen) {
      console.log('🎵 [LyricsStrategy] 进入歌词全屏模式');
      await this.fullscreenControls.enterFullscreen('lyrics');
    }
  }
  
  /**
   * 退出全屏
   * @returns {Promise<void>}
   */
  async exitFullscreen() {
    if (this.fullscreenControls && this.fullscreenControls.exitFullscreen) {
      console.log('🎵 [LyricsStrategy] 退出歌词全屏模式');
      await this.fullscreenControls.exitFullscreen('lyrics');
    }
  }
  
  /**
   * 清理资源
   */
  cleanup() {
    console.log('🧹 [LyricsStrategy] 开始清理资源');

    this._destroyed = true;
    this._loadToken += 1;
    
    // 停止歌词同步
    this.stopLyricsSync();
    
    // 清理歌词数据
    this.lyricsData = null;
    this.currentLyricIndex = -1;
    this.lyricsCallbacks = [];
    
    // 清理 AudioController
    if (this.audioController) {
      // 停止高频率时间更新
      if (this.audioController.stopHighFreqTimeUpdate) {
        this.audioController.stopHighFreqTimeUpdate();
      }
      
      // 清理 AudioController
      this.audioController.cleanup();
      this.audioController = null;
    }
    
    // 调用父类清理方法（清理事件监听器）
    super.cleanup();
    
    console.log('✅ [LyricsStrategy] 资源清理完成');
  }
}

